import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { MemberAvatar } from '@/components/MemberAvatar';
import PermissionsDialog from '@/components/PermissionsDialog';
import { AddUserDialog } from '@/components/members/AddUserDialog';
import { MembersMobileCards } from '@/components/members/MembersMobileCards';
import { LegacyBackfillDialog } from '@/components/members/LegacyBackfillDialog';
import { toast } from 'sonner';
import { MoreHorizontal, Users, UserPlus, Lock, Gift, Trash2, Mail, Pencil, LogIn, Bell, UserCog } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { EditMemberDialog } from '@/components/members/EditMemberDialog';

const EMPTY_USER_FORM = { name: '', email: '', password: '', role: 'member', company: '', phone: '', membership_status: 'trial' };

const ROLE_COLOR = {
  super_admin: 'bg-purple-100 text-purple-800',
  admin: 'bg-red-100 text-red-800',
  member: 'bg-blue-100 text-blue-800',
  viewer: 'bg-slate-100 text-slate-700',
};

function membershipColor(s) {
  if (s === 'free') return 'bg-purple-100 text-purple-800';
  if (s === 'active') return 'bg-emerald-100 text-emerald-800';
  return 'bg-amber-100 text-amber-800';
}

export default function MembersPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddUser, setShowAddUser] = useState(false);
  const [addFormInit, setAddFormInit] = useState({ ...EMPTY_USER_FORM });
  const [permissionsMember, setPermissionsMember] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    const t = searchParams.get('tab');
    return ['all', 'active', 'free'].includes(t) ? t : 'all';
  });

  // Keep URL ?tab=... in sync with the user's selection so links stay shareable.
  useEffect(() => {
    const current = searchParams.get('tab') || 'all';
    if (current !== activeTab) {
      const next = new URLSearchParams(searchParams);
      if (activeTab === 'all') next.delete('tab');
      else next.set('tab', activeTab);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchMembers = useCallback(() => {
    api.getMembers().then(r => setMembers(r.data)).catch(() => toast.error('Failed to load members')).finally(() => setLoading(false));
  }, []);
  const [showBackfill, setShowBackfill] = useState(false);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  const handleRoleChange = async (memberId, role) => {
    try { await api.updateMemberRole(memberId, role); toast.success('Role updated'); fetchMembers(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to update role'); }
  };

  const handleDelete = async (memberId) => {
    if (!window.confirm('Remove this member from your team? Any unused portion of their seat will be credited to your next invoice on the 1st.')) return;
    try {
      const { data } = await api.removeSeat(memberId);
      toast.success(data.message || 'Member removed');
      fetchMembers();
    } catch (e) {
      // Fallback to plain delete for legacy users without an active subscription
      if (e.response?.status === 403 || e.response?.status === 404) {
        try {
          await api.deleteMember(memberId);
          toast.success('Member removed');
          fetchMembers();
          return;
        } catch (e2) {
          toast.error(e2.response?.data?.detail || 'Failed to remove');
          return;
        }
      }
      toast.error(e.response?.data?.detail || 'Failed to remove');
    }
  };

  const handleToggleFree = async (memberId, currentStatus) => {
    const newStatus = currentStatus === 'free' ? 'trial' : 'free';
    try { await api.updateMembership(memberId, newStatus); toast.success(`Membership updated to ${newStatus}`); fetchMembers(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to update membership'); }
  };

  const handleSendPasswordReset = async (memberId, memberEmail) => {
    if (!window.confirm(`Send a password reset email to ${memberEmail}?`)) return;
    try {
      const { data } = await api.adminSendPasswordReset(memberId);
      toast.success(data.message || 'Reset email sent');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reset email');
    }
  };

  const handleImpersonate = async (m) => {
    if (!window.confirm(`Log in as ${m.name}? You'll see exactly what they see; click "Return to admin" in the top banner to come back.`)) return;
    try {
      await api.impersonateMember(m.id);
      toast.success(`Now signed in as ${m.name}`);
      // Hard reload so every page-level cache (auth/me, lists, sockets) is rebuilt.
      window.location.assign('/dashboard');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not impersonate user');
    }
  };

  const handleNudge = async (m) => {
    const msg = window.prompt(`Custom message (or leave blank for default):`, '');
    if (msg === null) return;
    try {
      await api.nudgeMember(m.id, msg);
      toast.success(`Nudge sent to ${m.name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send nudge');
    }
  };

  const isSuperAdmin = user?.role === 'super_admin';
  const isAdmin = user?.role === 'admin' || isSuperAdmin;
  const userId = user?.id || user?._id;

  const canEditPermissions = (m) => {
    if (m.id === userId) return false;
    if (isSuperAdmin && m.role !== 'super_admin') return true;
    if (user?.role === 'admin' && (m.role === 'member' || m.role === 'viewer')) return true;
    return false;
  };

  const canEditRole = (m) => {
    if (m.id === userId) return false;
    if (m.role === 'super_admin') return false;
    if (isSuperAdmin) return true;
    if (user?.role === 'admin' && m.role !== 'admin') return true;
    return false;
  };

  // Edit covers the broader scope of name/email/phone/company/password/avatar
  // and follows the same authority matrix as the backend.
  const canEditMember = (m) => {
    if (m.id === userId) return false;
    if (m.role === 'super_admin' && !isSuperAdmin) return false;
    if (user?.role === 'admin' && (m.role === 'admin' || m.role === 'super_admin')) return false;
    return isAdmin;
  };

  // Impersonation rules mirror the backend authority check.
  const canImpersonate = (m) => {
    if (m.id === userId) return false;
    if (m.role === 'super_admin') return false;
    if (isSuperAdmin) return true;
    if (user?.role === 'admin' && (m.role === 'member' || m.role === 'viewer')) return true;
    return false;
  };

  const freeMembers = members.filter(m => m.membership_status === 'free');
  const activeMembers = useMemo(() => {
    const thirtyDaysAgoMs = Date.now() - 30 * 24 * 60 * 60 * 1000;
    return members.filter(m => {
      const ts = m.last_active_at;
      if (!ts) return false;
      const t = new Date(ts).getTime();
      return Number.isFinite(t) && t >= thirtyDaysAgoMs;
    });
  }, [members]);
  const filteredMembers = activeTab === 'free'
    ? freeMembers
    : activeTab === 'active'
      ? activeMembers
      : members;

  const openCreateFreeAccount = () => {
    setAddFormInit({ ...EMPTY_USER_FORM, membership_status: 'free' });
    setShowAddUser(true);
  };

  const openAddUser = () => {
    setAddFormInit({ ...EMPTY_USER_FORM });
    setShowAddUser(true);
  };

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-8" data-testid="members-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium mb-1">Team</p>
          <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight">Members</h1>
          {user?.role === 'admin' && (
            <div
              className="inline-flex items-center gap-1.5 mt-1.5 px-2 py-0.5 rounded-sm bg-[#EFF6FF] dark:bg-blue-950/40 border border-[#0047AB]/15 dark:border-blue-900/40 text-[11px] text-[#0047AB] dark:text-blue-300 font-medium"
              data-testid="admin-team-chip"
              title="You see yourself and users you've added. Super admins see the full platform."
            >
              <Users className="w-3 h-3" /> Your team
            </div>
          )}
        </div>
        <div className="flex items-center flex-wrap gap-2 sm:gap-3">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-slate-400" />
            <span className="text-sm text-slate-500">{members.length} members</span>
          </div>
          {isSuperAdmin && (
            <Button onClick={() => setShowBackfill(true)} variant="outline" className="rounded-sm text-sm border-slate-300 text-slate-700 hover:bg-slate-50" data-testid="assign-owners-btn">
              <UserCog className="w-4 h-4 mr-1.5" /> Assign Owners
            </Button>
          )}
          {isSuperAdmin && (
            <Button onClick={openCreateFreeAccount} variant="outline" className="rounded-sm text-sm border-purple-300 text-purple-700 hover:bg-purple-50" data-testid="create-free-account-btn">
              <Gift className="w-4 h-4 mr-1.5" /> Create Free Account
            </Button>
          )}
          {isAdmin && (
            <Button onClick={openAddUser} className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm text-sm" data-testid="add-user-btn">
              <UserPlus className="w-4 h-4 mr-1.5" /> Add User
            </Button>
          )}
        </div>
      </div>

      {/* My Profile Picture */}
      <div className="mb-6 border border-slate-200 rounded-sm bg-white p-5 flex items-center gap-5">
        <MemberAvatar userId={userId} name={user?.name} size="lg" editable onUpload={fetchMembers} />
        <div>
          <h2 className="font-heading font-semibold text-slate-900 text-sm">Your Profile Picture</h2>
          <p className="text-xs text-slate-500 mt-0.5">Click the avatar to upload a new photo. JPEG, PNG, WebP or GIF up to 5MB.</p>
        </div>
      </div>

      {/* Tabs: All Members / Active / Free Accounts */}
      {isSuperAdmin && (
        <div className="mb-4">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-slate-100 rounded-sm h-9">
              <TabsTrigger value="all" className="rounded-sm text-xs data-[state=active]:bg-white" data-testid="tab-all-members">
                All Members <span className="ml-1.5 text-[10px] bg-slate-200 px-1.5 py-0.5 rounded-sm">{members.length}</span>
              </TabsTrigger>
              <TabsTrigger value="active" className="rounded-sm text-xs data-[state=active]:bg-white" data-testid="tab-active-members">
                <Users className="w-3 h-3 mr-1 text-emerald-600" /> Active <span className="ml-1.5 text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-sm">{activeMembers.length}</span>
              </TabsTrigger>
              <TabsTrigger value="free" className="rounded-sm text-xs data-[state=active]:bg-white" data-testid="tab-free-accounts">
                <Gift className="w-3 h-3 mr-1 text-purple-600" /> Free Accounts <span className="ml-1.5 text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-sm">{freeMembers.length}</span>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      )}

      {/* Free Accounts Info Banner */}
      {activeTab === 'free' && isSuperAdmin && (
        <div className="mb-4 p-4 bg-purple-50 border border-purple-200 rounded-sm flex items-start gap-3" data-testid="free-accounts-banner">
          <Gift className="w-5 h-5 text-purple-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-purple-900">Free Accounts ({freeMembers.length})</p>
            <p className="text-xs text-purple-700 mt-0.5">
              Free accounts have full CRM access with no membership fee or trial expiry. Use the <strong>&ldquo;Create Free Account&rdquo;</strong> button above to add one, or click <strong>&ldquo;Set Free&rdquo;</strong> in any member&apos;s action menu.
            </p>
          </div>
        </div>
      )}

      <MembersMobileCards
        loading={loading}
        members={filteredMembers}
        emptyState={activeTab === 'free' ? 'No free accounts yet — click "Create Free Account" to add one' : 'No members found'}
        isAdmin={isAdmin}
        isSuperAdmin={isSuperAdmin}
        userId={userId}
        canEditMember={canEditMember}
        canEditPermissions={canEditPermissions}
        canImpersonate={canImpersonate}
        setEditingMember={setEditingMember}
        setPermissionsMember={setPermissionsMember}
        handleImpersonate={handleImpersonate}
        handleNudge={handleNudge}
        handleToggleFree={handleToggleFree}
        handleSendPasswordReset={handleSendPasswordReset}
        handleDelete={handleDelete}
      />

      <div className="border border-slate-200 rounded-sm bg-white overflow-hidden hidden sm:block">
        <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F8FAFC]">
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500 font-medium">Name</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500 font-medium hidden sm:table-cell">Email</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500 font-medium">Role</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500 font-medium hidden md:table-cell">Membership</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500 font-medium hidden lg:table-cell">Joined</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500 font-medium hidden lg:table-cell">Last Active</TableHead>
              {isAdmin && <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500 font-medium w-12"></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400 text-sm">Loading...</TableCell></TableRow>
            ) : filteredMembers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-10">
                  {activeTab === 'free' ? (
                    <div>
                      <Gift className="w-8 h-8 text-purple-200 mx-auto mb-2" />
                      <p className="text-sm text-slate-400">No free accounts yet</p>
                      <p className="text-xs text-slate-300 mt-1">Click &ldquo;Create Free Account&rdquo; to add one</p>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400">No members found</p>
                  )}
                </TableCell>
              </TableRow>
            ) : filteredMembers.map(m => (
              <TableRow key={m.id} className="hover:bg-slate-50" data-testid={`member-row-${m.id}`}>
                <TableCell>
                  <div className="flex items-center gap-2.5">
                    <MemberAvatar userId={m.id} name={m.name} size="sm" />
                    <span className="text-sm font-medium text-slate-900">{m.name}</span>
                  </div>
                </TableCell>
                <TableCell className="text-sm text-slate-600 hidden sm:table-cell">{m.email}</TableCell>
                <TableCell>
                  {canEditRole(m) ? (
                    <Select value={m.role} onValueChange={v => handleRoleChange(m.id, v)}>
                      <SelectTrigger className="w-[110px] h-7 rounded-sm text-xs" data-testid={`role-select-${m.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {isSuperAdmin && <SelectItem value="admin">Admin</SelectItem>}
                        <SelectItem value="member">Member</SelectItem>
                        <SelectItem value="viewer">Viewer</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-sm ${ROLE_COLOR[m.role] || 'bg-slate-100 text-slate-700'}`}>
                      {m.role === 'super_admin' ? 'Super Admin' : m.role}
                    </span>
                  )}
                </TableCell>
                <TableCell className="hidden md:table-cell">
                  <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-sm ${membershipColor(m.membership_status)}`}>
                    {m.membership_status || 'inactive'}
                  </span>
                </TableCell>
                <TableCell className="text-sm text-slate-500 hidden lg:table-cell">
                  {m.created_at ? new Date(m.created_at).toLocaleDateString() : '\u2014'}
                </TableCell>
                <TableCell className="text-sm hidden lg:table-cell" data-testid={`last-active-${m.id}`}>
                  {(() => {
                    const ts = m.last_active_at;
                    if (!ts) return <span className="text-slate-400">Never</span>;
                    const ageMs = Date.now() - new Date(ts).getTime();
                    const days = Math.floor(ageMs / 86_400_000);
                    const hrs = Math.floor(ageMs / 3_600_000);
                    const mins = Math.floor(ageMs / 60_000);
                    const label = ageMs < 60_000
                      ? 'Just now'
                      : mins < 60 ? `${mins}m ago`
                      : hrs < 24 ? `${hrs}h ago`
                      : days < 30 ? `${days}d ago`
                      : new Date(ts).toLocaleDateString();
                    const isStale = days >= 7;
                    return <span className={isStale ? 'text-amber-700' : 'text-slate-500'}>{label}</span>;
                  })()}
                </TableCell>
                {isAdmin && (
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      {canEditMember(m) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-slate-500 hover:text-[#0047AB] hover:bg-blue-50"
                          onClick={() => setEditingMember(m)}
                          aria-label={`Edit ${m.name}`}
                          data-testid={`edit-member-btn-${m.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {m.id !== userId && m.role !== 'super_admin' && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`member-actions-${m.id}`}><MoreHorizontal className="w-4 h-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {canEditMember(m) && (
                              <DropdownMenuItem onClick={() => setEditingMember(m)} data-testid={`member-edit-${m.id}`}>
                                <Pencil className="w-4 h-4 mr-2" /> Edit
                              </DropdownMenuItem>
                            )}
                            {canEditPermissions(m) && (
                              <DropdownMenuItem onClick={() => setPermissionsMember(m)} data-testid={`member-permissions-${m.id}`}>
                                <Lock className="w-4 h-4 mr-2" /> Permissions
                              </DropdownMenuItem>
                            )}
                            {canImpersonate(m) && (
                              <DropdownMenuItem onClick={() => handleImpersonate(m)} data-testid={`member-impersonate-${m.id}`}>
                                <LogIn className="w-4 h-4 mr-2" /> Log in as {m.name.split(' ')[0]}
                              </DropdownMenuItem>
                            )}
                            {canEditMember(m) && (
                              <DropdownMenuItem onClick={() => handleNudge(m)} data-testid={`member-nudge-${m.id}`}>
                                <Bell className="w-4 h-4 mr-2" /> Nudge
                              </DropdownMenuItem>
                            )}
                            {isSuperAdmin && (
                              <DropdownMenuItem onClick={() => handleToggleFree(m.id, m.membership_status)} data-testid={`member-free-${m.id}`}>
                                <Gift className="w-4 h-4 mr-2" /> {m.membership_status === 'free' ? 'Remove Free' : 'Set Free'}
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => handleSendPasswordReset(m.id, m.email)} data-testid={`member-reset-password-${m.id}`}>
                              <Mail className="w-4 h-4 mr-2" /> Send Reset Email
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(m.id)} className="text-red-600" data-testid={`member-delete-${m.id}`}><Trash2 className="w-4 h-4 mr-2" /> Remove</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
      </div>

      <AddUserDialog open={showAddUser} onOpenChange={setShowAddUser} isSuperAdmin={isSuperAdmin} onCreated={fetchMembers} initialForm={addFormInit} />

      <EditMemberDialog
        open={!!editingMember}
        onOpenChange={(open) => { if (!open) setEditingMember(null); }}
        member={editingMember}
        currentUser={user}
        onSaved={fetchMembers}
      />

      <PermissionsDialog
        open={!!permissionsMember}
        onOpenChange={(open) => { if (!open) setPermissionsMember(null); }}
        member={permissionsMember}
        currentUserRole={user?.role}
      />

      {isSuperAdmin && (
        <LegacyBackfillDialog
          open={showBackfill}
          onOpenChange={setShowBackfill}
          members={members}
          onAssigned={fetchMembers}
        />
      )}
    </div>
  );
}
